import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import { TextField, Tooltip } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { RichTreeView } from '@mui/x-tree-view/RichTreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import type { TreeItemProps } from '@mui/x-tree-view/TreeItem'
import { useTreeItemModel } from '@mui/x-tree-view/hooks'
import React from 'react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/material.css'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import { isCategory, useUserFormModel } from './user-form-model'
import type { PrivilegeNode, PrivilegePermission } from './user-form-model'

const treeSx: SxProps<Theme> = {
    '& .MuiTreeItem-content': {
        gap: '6px',
        padding: '6px 8px',
        borderRadius: '12px',
        color: 'rgb(var(--k-ink))',
    },
    '& .MuiTreeItem-content:hover': { backgroundColor: 'rgb(var(--k-hover))' },
    '& .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content.Mui-selected:hover, & .MuiTreeItem-content.Mui-focused': {
        backgroundColor: 'transparent',
    },
    '& .MuiTreeItem-content.Mui-focusVisible': { outline: '3px solid rgb(75 68 216 / 0.3)' },
    '& .MuiTreeItem-iconContainer': {
        width: 34,
        height: 34,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '10px',
        color: 'rgb(var(--k-muted))',
        cursor: 'pointer',
    },
    '& .MuiTreeItem-iconContainer svg': { fontSize: 26 },
    '& .MuiTreeItem-iconContainer:hover': { backgroundColor: 'rgb(var(--k-line))', color: 'rgb(var(--k-ink))' },
    '& .privilege-role': { cursor: 'pointer' },
    '& .MuiTreeItem-checkbox': { padding: '2px', color: 'rgb(var(--k-muted))' },
    '& .MuiTreeItem-checkbox.Mui-checked': { color: 'rgb(var(--k-primary))' },
    '& .MuiTreeItem-checkbox .MuiSvgIcon-root': { fontSize: 28 },
    '& .MuiTreeItem-groupTransition': {
        marginLeft: '18px',
        paddingLeft: '10px',
        borderLeft: '1px dashed rgb(var(--k-line-strong))',
    },
    '& .privilege-category > .MuiTreeItem-content': {
        cursor: 'default',
        marginTop: '6px',
        padding: '10px 12px',
        backgroundColor: 'rgb(var(--k-sunken))',
        border: '1px solid rgb(var(--k-line-soft))',
    },
    '& .privilege-category > .MuiTreeItem-content:hover': { backgroundColor: 'rgb(var(--k-sunken))' },
    '& .privilege-category:first-of-type > .MuiTreeItem-content': { marginTop: 0 },
}

const NoCheckbox = () => null

const RolePermissions = ({ permisos }: { permisos: PrivilegePermission[] }) => (
    <div className="flex max-w-[360px] flex-col gap-2 p-1">
        <p className="m-0 text-sm font-bold">Permisos otorgados</p>
        {permisos.length === 0 && <p className="m-0 text-xs">Este rol no otorga permisos adicionales.</p>}
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {permisos.map((permiso) => (
                <li key={permiso.id} className="text-xs leading-[1.45]">
                    <span className="font-bold">{permiso.name}:</span> {permiso.description}
                </li>
            ))}
        </ul>
    </div>
)

const CategoryLabel = ({ node }: { node: Extract<PrivilegeNode, { kind: 'category' }> }) => (
    <span className="flex min-w-0 items-center gap-3">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] ${node.tone}`}>
            <node.Icon style={{ fontSize: 20 }} />
        </span>
        <span className="flex-1 text-[13px] font-extrabold uppercase tracking-[0.06em] text-k-ink">{node.label}</span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${node.selected > 0 ? 'bg-k-accent-soft text-k-accent-ink' : 'bg-k-line-soft text-k-muted'}`}>
            {node.selected} de {node.total}
        </span>
    </span>
)

const RoleLabel = ({ node }: { node: Extract<PrivilegeNode, { kind: 'role' }> }) => (
    <span className="flex min-w-0 items-center gap-2">
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className={`text-sm text-k-ink ${node.children.length > 0 ? 'font-bold' : 'font-semibold'}`}>{node.label}</span>
            {node.description && <span className="text-xs text-k-muted">{node.description}</span>}
        </span>
        <Tooltip placement="left" title={<RolePermissions permisos={node.permisos} />}>
            <span className="inline-flex shrink-0 text-k-muted-2" onClick={(event) => event.stopPropagation()}>
                <HelpOutlineIcon style={{ fontSize: 18 }} />
            </span>
        </Tooltip>
    </span>
)

const ToggleRoleContext = React.createContext<(role: string) => void>(() => undefined)

/** Clics que ya tienen su propia acción: la flecha expande y la casilla selecciona por sí misma. */
const IGNORED_ROW_TARGETS = '.MuiTreeItem-iconContainer, .MuiTreeItem-checkbox'

const PrivilegeTreeItem = React.forwardRef<HTMLLIElement, TreeItemProps>((props, ref) => {
    const node = useTreeItemModel<PrivilegeNode>(props.itemId)
    const toggleRole = React.useContext(ToggleRoleContext)
    if (node?.kind === 'category') {
        return (
            <TreeItem
                {...props}
                ref={ref}
                className={`privilege-category ${props.className || ''}`}
                label={<CategoryLabel node={node} />}
                slots={{ ...props.slots, checkbox: NoCheckbox }}
            />
        )
    }
    const content = (props.slotProps?.content || {}) as React.HTMLAttributes<HTMLDivElement>
    return (
        <TreeItem
            {...props}
            ref={ref}
            label={node ? <RoleLabel node={node} /> : props.label}
            slotProps={{
                ...props.slotProps,
                content: {
                    ...content,
                    className: `privilege-role ${content.className || ''}`,
                    onClick: (event: React.MouseEvent<HTMLDivElement>) => {
                        content.onClick?.(event)
                        if ((event.target as HTMLElement).closest(IGNORED_ROW_TARGETS)) return
                        toggleRole(props.itemId)
                    },
                },
            }}
        />
    )
})
PrivilegeTreeItem.displayName = 'PrivilegeTreeItem'

const UserForm = () => {
    const model = useUserFormModel()
    const { form, change } = model

    return (
        <KarteraAdminTemplate loading={false} title={model.title}>
            <form onSubmit={model.save} className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.busy && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title={model.title}
                    description={model.isEdit ? 'Actualiza los datos y privilegios del usuario.' : 'Al guardar, el usuario recibirá un correo para activar su cuenta.'}
                    backTo={model.backTo}
                    backLabel="Usuarios"
                    actions={model.status ? (
                        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${model.status.tone}`}>{model.status.label}</span>
                    ) : (
                        <span className="text-[13px] text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>
                    )}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Datos generales</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-3">
                        <TextField
                            className="v2-field md:col-span-3" variant="filled" fullWidth required
                            type="email"
                            label="Correo"
                            disabled={model.isEdit}
                            helperText={model.isEdit ? 'Es el usuario para iniciar sesión; no se puede cambiar.' : 'A este correo llegará la invitación para activar la cuenta.'}
                            value={form.correo}
                            onChange={(event) => change.correo(event.target.value)}
                        />
                        <TextField className="v2-field" variant="filled" fullWidth required label="Nombre(s)" value={form.nombres} onChange={(event) => change.nombres(event.target.value)} />
                        <TextField className="v2-field" variant="filled" fullWidth required label="Apellido paterno" value={form.apPat} onChange={(event) => change.apPat(event.target.value)} />
                        <TextField className="v2-field" variant="filled" fullWidth required label="Apellido materno" value={form.apMat} onChange={(event) => change.apMat(event.target.value)} />
                        <div className="v2-phone">
                            <span className="v2-phone-label">Celular<span>*</span></span>
                            <PhoneInput
                                country="mx"
                                preferredCountries={['mx', 'us']}
                                disableDropdown
                                specialLabel=""
                                value={form.celular}
                                onChange={(value) => change.celular(value)}
                                inputProps={{ required: true, name: 'celular' }}
                            />
                        </div>
                    </div>
                </section>

                <section className="k-card">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex flex-col gap-1">
                            <h2 className="k-card-title">Privilegios</h2>
                            <p className="m-0 text-sm text-k-muted">Por seguridad, da solo los accesos mínimos necesarios para que el usuario cumpla sus tareas.</p>
                        </div>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-k-accent-soft px-2.5 py-1 text-xs font-bold text-k-accent-ink">
                            <ShieldOutlinedIcon style={{ fontSize: 14 }} />
                            {model.selectedCount} {model.selectedCount === 1 ? 'rol asignado' : 'roles asignados'}
                        </span>
                    </div>

                    <div className="rounded-[14px] border border-k-line p-2">
                        <ToggleRoleContext.Provider value={model.toggleRole}>
                            <RichTreeView<PrivilegeNode, true>
                                items={model.privilegeTree}
                                multiSelect
                                checkboxSelection
                                expansionTrigger="iconContainer"
                                selectedItems={model.selectedTreeIds}
                                onSelectedItemsChange={(_event, ids) => model.changeTreeSelection(ids)}
                                expandedItems={model.expanded}
                                onExpandedItemsChange={(_event, ids) => model.changeExpanded(ids)}
                                isItemSelectionDisabled={isCategory}
                                getItemId={(item) => item.id}
                                getItemLabel={(item) => item.label}
                                slots={{ item: PrivilegeTreeItem }}
                                sx={treeSx}
                            />
                        </ToggleRoleContext.Provider>
                    </div>
                </section>

                <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                    <button
                        type="button"
                        className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        onClick={model.cancel}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={model.busy}
                        className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <CheckIcon />
                        Guardar usuario
                    </button>
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default UserForm
